import type { TFunction } from 'i18next'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { RoleName } from '@/components/RoleName'
import { Banner } from '@/components/feedback/Banner'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { useSession } from '@/features/auth/api/useSession'
import { mayReadRoles, useRoles } from '@/features/settings/api/useRoles'
import {
  useCreateUser,
  useResetUserPassword,
  useSetUserActive,
  useUpdateUser,
  useUsers,
} from '@/features/settings/api/useUsers'
import { capabilityLabelKey } from '@/features/settings/capabilityLabels'
import { GeneratedCredentialDialog } from '@/features/settings/components/GeneratedCredentialDialog'
import { UserFormSheet, type UserDraft } from '@/features/settings/components/UserFormSheet'
import { DIRECTORY_GRANTS } from '@/features/settings/userGrants'
import { userRefusalMessage } from '@/features/settings/userRefusals'
import { cn } from '@/lib/cn'
import type { Capability, CreatedUser, DirectoryUser } from '@/types/domain'

/* The account list — Settings «Пользователи» (S-7; Directories → Users before).
 *
 * It moved beside «Роли и доступ» so that who a person is and what their role
 * may do are one place (A-1). The tab is offered to a holder of
 * `admin.users.manage`, the guard on every route it calls.
 *
 * A table from `md` up and a card per account below it: a narrow screen
 * changes the pattern rather than scrolling a grid sideways
 * (frontend/README.md). Both forms are built from the same cell helpers, so a
 * phone never loses an act the desktop row offers. */

/** `null` closed, `'new'` adding, a record editing — see DirectoriesPage. */
type EditTarget<T> = T | 'new' | null

const HEAD = 'asa-eyebrow h-9 px-3 first:pl-[18px] last:pr-[18px]'
const CELL = 'px-3 first:pl-[18px] last:pr-[18px] text-[13px]'
const ROW = 'border-line-hair h-[38px] border-b last:border-b-0'
/** A row that opens its own editor. Whole rows are clickable, so the target is
 *  the full 38px height rather than a 14px pencil the mouse has to find. */
const EDITABLE_ROW = 'hover:bg-surface-hover cursor-pointer transition-colors'

export function UsersPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useUsers()
  const { data: session } = useSession()
  // For the role tag's tone only: the superuser is a FLAG on a role record,
  // never a role name — a founder may rename it, and a custom role may hold it.
  const { data: roles } = useRoles({ enabled: mayReadRoles(session) })
  const [target, setTarget] = useState<EditTarget<DirectoryUser>>(null)

  /* The generated credential, held only between the mutation resolving and the
     admin acknowledging it. Never written to a query cache: the point of a
     value that is readable once is that nothing keeps it. */
  const [credential, setCredential] = useState<CreatedUser | null>(null)

  const create = useCreateUser()
  const update = useUpdateUser()
  const setActive = useSetUserActive()
  const resetPassword = useResetUserPassword()

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  const record = target !== null && target !== 'new' ? target : undefined
  const pending = create.isPending || update.isPending

  /* The server refuses these two for your own account, and says so clearly. The
     client withholds the controls as well, because an action that is always
     going to be refused should not be offered — absence is preferred
     to a disabled control that explains itself only after being pressed. */
  const isSelf = (user: DirectoryUser) => user.id === session?.id

  const superuserRoles = new Set(
    (roles ?? []).filter((role) => role.isSuperuser).map((role) => role.key),
  )

  /* The sheet shows the save's refusal, so opening it clears the last one: a
     sheet that opens already saying "refused" is showing a previous open's
     error. */
  const openSheet = (next: DirectoryUser | 'new') => {
    create.reset()
    update.reset()
    setTarget(next)
  }
  const saveError = record ? update.error : create.error

  const submit = (values: UserDraft) => {
    if (record) {
      // Email is absent: the server does not accept a changed address on an
      // amendment, and the form renders it read-only for the same reason.
      update.mutate(
        {
          id: record.id,
          payload: {
            name: values.name,
            role: values.role,
            office: values.office,
            language: values.language,
            capabilities: values.capabilities,
          },
        },
        { onSuccess: () => setTarget(null) },
      )
      return
    }

    create.mutate(values, {
      onSuccess: (result) => {
        setTarget(null)
        // Straight into the hand-off: this is the only moment the credential
        // exists outside the admin's clipboard.
        setCredential(result)
      },
    })
  }

  /* The authority rule (S-7, O7): an account that outranks the caller is
     listed, and every act on it is refused with the reason rather than hidden
     — the server answers 403 regardless. `=== false`, so a server that does
     not send the flag yet leaves the row as it was. */
  const outrankedReason = (user: DirectoryUser) =>
    user.manageable === false ? t('directories.users.outranked') : undefined

  const roleTag = (user: DirectoryUser) => (
    <Tag tone={superuserRoles.has(user.role) ? 'brand' : 'neutral'}>
      <RoleName role={user.role} />
    </Tag>
  )

  /* The account's OWN grants, beyond what its role gives — the set the card
     edits. Not the effective set: a role-derived permission listed here would
     read as one somebody granted this person and could take away. */
  const extrasOf = (user: DirectoryUser) =>
    user.userCapabilities.length > 0
      ? user.userCapabilities.map((key) => extraLabel(key, t)).join(', ')
      : undefined

  const status = (user: DirectoryUser) => (
    <div className="flex items-center gap-1.5">
      <StatusPill tone={user.isActive ? 'positive' : 'neutral'}>
        {user.isActive ? t('common.form.active') : t('directories.users.inactive')}
      </StatusPill>
      {/* Says the credential is still the generated one, which is what a
          founder chasing a colleague needs to know. */}
      {user.isActive && user.mustChangePassword ? (
        <Tag>{t('directories.users.awaitingPasswordChange')}</Tag>
      ) : null}
    </div>
  )

  const actions = (user: DirectoryUser) => {
    const outranked = outrankedReason(user)
    return (
      <>
        {/* The row's name opens the editor too; this is the act named, and the
            one that can carry a refusal's reason. */}
        <Button
          size="sm"
          variant="ghost"
          blockedReason={outranked}
          onClick={() => openSheet(user)}
        >
          {t('common.actions.edit')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          blockedReason={outranked}
          onClick={() =>
            resetPassword.mutate(user.id, {
              onSuccess: (result) => setCredential(result),
            })
          }
          disabled={resetPassword.isPending}
        >
          {t('directories.users.resetPassword')}
        </Button>
        {isSelf(user) ? null : (
          <Button
            size="sm"
            variant="ghost"
            blockedReason={outranked}
            onClick={() => setActive.mutate({ id: user.id, isActive: !user.isActive })}
            disabled={setActive.isPending}
          >
            {user.isActive ? t('directories.users.deactivate') : t('directories.users.activate')}
          </Button>
        )}
      </>
    )
  }

  return (
    <Card>
      <CardHeader
        action={
          <Button size="sm" iconLeft={Plus} onClick={() => openSheet('new')}>
            {t('directories.users.new')}
          </Button>
        }
      >
        <CardTitle>{t('directories.users.title')}</CardTitle>
      </CardHeader>

      {/* A mutation that fails must say so where it was triggered. Without this
          a refused deactivation — the last super-admin, or your own account —
          looks like a button that did nothing. An account that outranks the
          caller (S-7, O7) is worded as that, not as a section the caller's
          role may not open — they are standing in it; your own account and
          the last super-admin by their tokens (S-13). Every refusal is the
          same dismissible Banner: the `ErrorState` that stood here for the
          rest offered «Повторить», which only cleared the error and sent
          nothing. A create or an update is not listed: its refusal belongs to
          the sheet the save was pressed in (`failure`), and a copy here showed
          it twice and outlived the sheet. */}
      {[setActive, resetPassword].map((mutation, index) =>
        mutation.error ? (
          <div key={index} className="px-3.5 pb-2" role="alert">
            <Banner
              tone="attention"
              title={userRefusalMessage(mutation.error, t)}
              action={
                <Button size="sm" variant="secondary" onClick={() => mutation.reset()}>
                  {t('common.actions.dismiss')}
                </Button>
              }
            />
          </div>
        ) : null,
      )}

      {/* `relative` contains the actions header's `sr-only` label: it is
          absolutely positioned, and with no positioned ancestor it escaped
          this scroll box and widened the PAGE (the S-7 browser check measured
          789px of document at a 390px viewport). */}
      <div className="asa-scroll relative hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className={HEAD}>{t('directories.users.name')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.email')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.role')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.office')}</th>
              <th scope="col" className={HEAD}>{t('directories.users.extras')}</th>
              <th scope="col" className={HEAD}>{t('common.fields.status')}</th>
              <th scope="col" className={HEAD}>
                <span className="sr-only">{t('common.actions.label')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((user) => (
              <tr key={user.id} className={ROW}>
                {outrankedReason(user) ? (
                  <td className={cn(CELL, 'text-text-heading')}>{user.name}</td>
                ) : (
                  <td
                    className={cn(CELL, 'text-text-heading', EDITABLE_ROW)}
                    onClick={() => openSheet(user)}
                    tabIndex={0}
                    role="button"
                    aria-label={t('directories.editRecord', { name: user.name })}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openSheet(user)
                      }
                    }}
                  >
                    {user.name}
                  </td>
                )}
                <td className={cn(CELL, 'text-text-muted text-[12.5px]')}>{user.email}</td>
                <td className={CELL}>{roleTag(user)}</td>
                <td className={cn(CELL, 'text-text-body')}>{user.office}</td>
                <td className={cn(CELL, 'text-text-muted text-[12.5px]')}>
                  {extrasOf(user) ?? '—'}
                </td>
                <td className={CELL}>{status(user)}</td>
                <td className={cn(CELL, 'text-right whitespace-nowrap')}>
                  <div className="flex justify-end gap-1.5">{actions(user)}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Phone: a row becomes a card, never a table scrolled sideways. The
          name, the role, the status and every act stay on the card, and the
          editor is the same sheet. */}
      <ul aria-label={t('directories.users.title')} className="flex flex-col md:hidden">
        {data.map((user) => {
          const extras = extrasOf(user)
          return (
            <li
              key={user.id}
              className="border-line-hair flex flex-col gap-2 border-b px-4 py-3 last:border-b-0"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                {outrankedReason(user) ? (
                  <span className="text-text-heading text-[13.5px]">{user.name}</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => openSheet(user)}
                    aria-label={t('directories.editRecord', { name: user.name })}
                    className="text-text-heading cursor-pointer self-start text-left text-[13.5px]"
                  >
                    {user.name}
                  </button>
                )}
                {/* break-all: an address has no spaces to wrap at, and one
                    long enough would push the card past the screen. */}
                <span className="text-text-muted text-[12px] font-light break-all">
                  {user.email} · {user.office}
                </span>
                {extras ? (
                  <span className="text-text-muted text-[12px] font-light">
                    {t('directories.users.extras')}: {extras}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {roleTag(user)}
                {status(user)}
              </div>
              <div className="flex flex-wrap gap-1.5">{actions(user)}</div>
            </li>
          )
        })}
      </ul>

      <UserFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={record}
        pending={pending}
        failure={saveError ?? undefined}
        onSubmit={submit}
      />

      <GeneratedCredentialDialog result={credential} onClose={() => setCredential(null)} />
    </Card>
  )
}

/** One own grant in the list's words: the card's short label for the three
 *  boxes, the role editor's sentence for any other key, the key itself for one
 *  this client has no words for. */
function extraLabel(capability: Capability, t: TFunction): string {
  const box = DIRECTORY_GRANTS.find((grant) => grant.capability === capability)
  if (box) return t(box.labelKey)
  const labelKey = capabilityLabelKey(capability)
  return labelKey ? t(labelKey) : capability
}
